import { useCallback, useState } from "react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { IAccountReceivableFinancials } from "Dashboard/scenes/Projects/domain/IAccountReceivableFinancials";
import { IAccountPayableFinancials } from "Dashboard/scenes/Projects/domain/IAccountPayableFinancials";
import { FormatNumber, FormatNumberWithUnitOrNull } from "utils/numberUtils";
import { FormatDateShort } from "utils/dateUtils";
import ToolTipPortal from "components/ToolTipPortal/ToolTipPortal";
import InfoTooltip from "components/Popup/InfoTooltip";
import { hasWriteAccessTo } from "utils/authUtil";
import { useAppDispatch } from "hooks/hooks";
import { useSelector } from "react-redux";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { HIGH_RISE_SALES_MODULE_NAME } from "Dashboard/scenes/HighRiseSales/domain/HighRiseSalesModule";
import { PARTNER_MODULE_NAME } from "Dashboard/scenes/Partners/domain/PartnerModule";
import { debounce } from "lodash";
import { partnersService } from "Dashboard/scenes/Partners/services/partner.service";
import { updateAccountPayableNote, updateAccountReceivableNote } from "../financials.slice";

interface IProps {
  receivables: IAccountReceivableFinancials | undefined;
  payables: IAccountPayableFinancials | undefined;
}

enum NoteType {
  AccountPayable,
  AccountReceivable,
}

interface TooltipState {
  isOpen: boolean;
  top: number;
  left: number;
  value: string;
  entryId: number;
  type: NoteType | undefined;
  index: number;
}

const initialState = {
  isOpen: false,
  top: 0,
  left: 0,
  value: "",
  entryId: 0,
  type: undefined,
  index: -1,
};

const ReceivablesAndPayables = ({ receivables, payables }: IProps) => {
  const [tooltip, setTooltip] = useState<TooltipState>(initialState);
  const dispatch = useAppDispatch();
  const { user } = useSelector(selectStateAuth);

  const close = () => {
    setTooltip(initialState);
  };

  const delayedNoteUpdate = useCallback(
    debounce(
      async (
        entryId: number,
        index: number,
        type: NoteType | undefined,
        value: string
      ) => {
        try {
          if (type === NoteType.AccountPayable) {
            await partnersService.updateAccountPayable({
              accountPayableId: entryId,
              note: value,
            });
            dispatch(updateAccountPayableNote({ index, note: value }));
          }
          if (type === NoteType.AccountReceivable) {
            await partnersService.updateAccountReceivable({
              accountReceivableId: entryId,
              note: value,
            });
            dispatch(updateAccountReceivableNote({ index, note: value }));
          }
          console.log(entryId, value);
        } catch (err) {
          console.log(err);
        }
      },
      1000), []
  );

  return (
    <div className="receivables-and-payables-box">
      <h3><strong>POTRAŽIVANJA / DUGOVANJA</strong></h3>
      <section>
        <div className="buildings-box-header">
          <div>
            <span className="right-align flexBasis75">potražujemo suma</span>
            <span className="strong-txt center-align txt-underline">
              {receivables && FormatNumberWithUnitOrNull(receivables.receivableAmount, "rsd")}
            </span>
          </div>
          <div>
            <span className="right-align flexBasis75">uplaćeno suma</span>
            <span className="strong-txt center-align txt-underline">
              {receivables && FormatNumberWithUnitOrNull(receivables.paidAmount, "rsd")}
            </span>
          </div>
        </div>
        <div className="buildings-box-content">
          <div className="list-wrapp" onScroll={() => close()}>
            {receivables?.accountReceivableEntries &&
              receivables.accountReceivableEntries.map((entry, index) => (
                <article key={index}>
                  <div className="col-2">
                    <InfoTooltip
                      tooltip={entry.partner.shortName}
                      classesProp="strong text-overflow"
                      element="span"
                    >{entry.partner.shortName}
                    </InfoTooltip>
                    <button
                      onClick={(e) => {
                        setTooltip({
                          isOpen: true,
                          top: e.clientY,
                          left: e.clientX,
                          value: entry.note,
                          entryId: entry.receivableId,
                          type: NoteType.AccountReceivable,
                          index,
                        });
                      }}
                    >
                      <HelpOutlineIcon className="far" fontSize="small" />
                    </button>
                  </div>
                  <div className="larger-first-cell">
                    <InfoTooltip element="span"
                      tooltip={`preostalo: ${FormatNumber(entry.receivableAmount - entry.paidAmount)}`}
                      classesProp={`box-span right larger-cell ${entry.paidAmount < entry.receivableAmount ? "important-price" : ""}`}>
                      {FormatNumber(entry.receivableAmount)}
                    </InfoTooltip>
                    <span className="box-span">{FormatDateShort(entry.date)}</span>
                    <span className="light-color">fakturisano</span>
                  </div>
                </article>
              ))}
          </div>
        </div>
      </section>
      <section>
        <div className="buildings-box-header">
          <div>
            <span className="right-align flexBasis75">fakturisano suma</span>
            <span className="strong-txt center-align txt-underline">
              {payables && FormatNumberWithUnitOrNull(payables.invoicedAmount, "rsd")}
            </span>
          </div>
          <div>
            <span className="right-align flexBasis75">isplatili suma</span>
            <span className="strong-txt center-align txt-underline">
              {payables && FormatNumberWithUnitOrNull(payables.paidOffAmount, "rsd")}
            </span>
          </div>
        </div>
        <div className="buildings-box-content">
          <div className="list-wrapp">
            {payables?.accountPayableEntries &&
              payables.accountPayableEntries.map((entry, index) => (
                <article key={index}>
                  <div className="col-2">
                    <InfoTooltip tooltip={entry.partnerName}
                      classesProp="strong text-overflow"
                      element="span">
                      {entry.partnerName}
                    </InfoTooltip>
                    <button
                      onClick={(e) => {
                        setTooltip({
                          isOpen: true,
                          top: e.clientY,
                          left: e.clientX,
                          value: entry.note,
                          entryId: entry.payableId,
                          type: NoteType.AccountPayable,
                          index,
                        });
                      }}
                    >
                      <HelpOutlineIcon className="far" fontSize="small" />
                    </button>
                  </div>
                  <div className="larger-first-cell">
                    <InfoTooltip element="span"
                      tooltip={`preostalo: ${FormatNumber(entry.invoicedAmount - entry.paidOffAmount)}`}
                      classesProp={`box-span right ${entry.paidOffAmount < entry.invoicedAmount ? "important-price" : ""}`}>
                      {FormatNumber(entry.invoicedAmount)}
                    </InfoTooltip>
                    <span className="box-span">{FormatDateShort(entry.date)}</span>
                    <span className="light-color">dugujemo</span>
                  </div>
                </article>
              ))}
          </div>
        </div>
      </section>
      <ToolTipPortal
        {...tooltip}
        onClose={close}
        text="Napomena"
        offset={{ top: -52, left: 10 }}
      >
        <textarea
          readOnly={hasWriteAccessTo(user, HIGH_RISE_SALES_MODULE_NAME)
            && hasWriteAccessTo(user, PARTNER_MODULE_NAME)}
          defaultValue={tooltip.value}
          onChange={(e) => {
            delayedNoteUpdate(
              tooltip.entryId,
              tooltip.index,
              tooltip.type,
              e.target.value
            );
          }}
        ></textarea>
      </ToolTipPortal>
    </div>
  );
};

export default ReceivablesAndPayables;
